import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Country } from "country-state-city";
import { ChevronDown, X } from "lucide-react";
import useRequest from "../../../components/hook/use-req";
import { TeamDetails } from "../../../components/crm/customer/types";
import Pagination from "../../../components/pagination";
import Input from "../../../components/input";
import { isConversationAgent } from "../../../utils/org-role";

const SearchIcon = () => (
  <svg
    aria-hidden="true"
    className="h-5 w-5 text-slate-900 dark:text-slate-300"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35" />
    <circle cx="11" cy="11" r="7" />
  </svg>
);

const FilterIcon = () => (
  <svg
    aria-hidden="true"
    className="h-5 w-5 text-slate-900 dark:text-slate-300"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 5h18l-7 8v5l-4 2v-7L3 5Z"
    />
  </svg>
);

const UsersIcon = () => (
  <svg
    aria-hidden="true"
    className="h-5 w-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16 19v-1.5A3.5 3.5 0 0 0 12.5 14h-5A3.5 3.5 0 0 0 4 17.5V19"
    />
    <circle cx="10" cy="7" r="4" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M20 19v-1a3 3 0 0 0-2.5-3" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 4a3 3 0 0 1 0 6" />
  </svg>
);

const ArrowUpIcon = () => (
  <svg
    aria-hidden="true"
    className="h-4 w-4 text-indigo-300"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 19V5m0 0-5 5m5-5 5 5" />
  </svg>
);

const MoreIcon = () => (
  <svg
    aria-hidden="true"
    className="h-5 w-5 text-indigo-700 dark:text-indigo-400"
    fill="currentColor"
    viewBox="0 0 20 20"
  >
    <path d="M10 4a1.75 1.75 0 1 0 0-3.5A1.75 1.75 0 0 0 10 4ZM10 11.75a1.75 1.75 0 1 0 0-3.5 1.75 1.75 0 0 0 0 3.5ZM10 19.5a1.75 1.75 0 1 0 0-3.5 1.75 1.75 0 0 0 0 3.5Z" />
  </svg>
);

const STATUS_STYLE: Record<string, string> = {
  active: "bg-emerald-50 text-emerald-700",
  pending: "bg-orange-50 text-orange-700",
};

const StatusBadge = ({ status }: { status?: string }) => {
  if (!status) return <span className="text-xs text-slate-400 dark:text-slate-500">—</span>;
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium capitalize ${
        STATUS_STYLE[status.toLowerCase()] || "bg-slate-100 text-slate-600"
      }`}
    >
      {status}
    </span>
  );
};

const Avatar = ({ name }: { name: string }) => {
  const getInitials = (fullName: string) => {
    const parts = fullName.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return fullName.slice(0, 2).toUpperCase();
  };

  const initials = getInitials(name);

  return (
    <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-indigo-400">
      <span className="text-xs font-medium text-white">{initials}</span>
    </div>
  );
};

// ─── Org roles — the only 4 real roles the backend accepts (Eben 2026-08-03),
// "business_user" is always the fixed base `role` sent alongside these. ──────
const ORG_ROLES: { value: string; label: string }[] = [
  { value: "campaign_manager", label: "Campaign Manager" },
  { value: "marketing_manager", label: "Marketing Manager" },
  { value: "conversation_agent", label: "Conversation Agent" },
  { value: "crm_admin", label: "CRM Admin" },
  { value: "admin", label: "Admin" },
];

const ORG_ROLE_LABEL: Record<string, string> = Object.fromEntries(
  ORG_ROLES.map((r) => [r.value, r.label])
);

// ─── Dial code list (same pattern as update-customer.tsx / lead-qr-section.tsx) ─
const normalizeDialCode = (phoneCode?: string | number | null) => {
  if (!phoneCode) return "+234";
  const code = String(phoneCode);
  return code.startsWith("+") ? code : `+${code}`;
};

const countryCodeOptions = Country.getAllCountries()
  .map((country) => ({
    value: normalizeDialCode(country.phonecode),
    label: `${normalizeDialCode(country.phonecode)} ${country.isoCode}`,
    isoCode: country.isoCode,
  }))
  .sort((a, b) => a.value.localeCompare(b.value));

const getCountryByDialCode = (dialCode?: string | number | null) =>
  countryCodeOptions.find((c) => c.value === normalizeDialCode(dialCode));

// ─── Country-code dropdown — flag + dial code button with a searchless list ──
const CountryCodeSelect: React.FC<{
  value: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = getCountryByDialCode(value);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="flex h-[45px] items-center gap-1.5 rounded border-2 border-[#E5E7EB] dark:border-gray-700 px-2.5 text-sm text-[#434343] dark:text-gray-300 bg-white dark:bg-gray-900"
      >
        {selected && (
          <img
            src={`https://flagcdn.com/24x18/${selected.isoCode.toLowerCase()}.png`}
            alt=""
            className="h-3.5 w-5 rounded-sm object-cover"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
          />
        )}
        <span>{value}</span>
        <ChevronDown size={14} className={`text-[#7B7B7B] font-semibold dark:text-gray-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 max-h-56 w-56 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg">
          {countryCodeOptions.map((option) => (
            <button
              key={option.isoCode}
              type="button"
              onClick={() => { onChange(option.value); setOpen(false); }}
              className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800 ${
                option.value === value ? "bg-indigo-50 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-400" : "text-gray-700 dark:text-gray-300"
              }`}
            >
              <img
                src={`https://flagcdn.com/24x18/${option.isoCode.toLowerCase()}.png`}
                alt=""
                className="h-3.5 w-5 rounded-sm object-cover"
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
              />
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

interface InviteFormState {
  employeeName: string;
  email: string;
  countryCode: string;
  phone: string;
  orgRole: string;
}

const emptyInviteForm = (defaultCountryCode: string): InviteFormState => ({
  employeeName: "",
  email: "",
  countryCode: defaultCountryCode,
  phone: "",
  orgRole: "",
});

// ─── Invite Members modal ─────────────────────────────────────────────────────
const InviteModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onSubmit: (form: InviteFormState) => Promise<boolean>;
  defaultCountryCode: string;
  loading: boolean;
}> = ({ open, onClose, onSubmit, defaultCountryCode, loading }) => {
  const [form, setForm] = useState<InviteFormState>(() => emptyInviteForm(defaultCountryCode));
  const [touched, setTouched] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);
  const roleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setForm(emptyInviteForm(defaultCountryCode));
      setTouched(false);
    }
  }, [open, defaultCountryCode]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (roleRef.current && !roleRef.current.contains(e.target as Node)) setRoleOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!open) return null;

  const isMissing = (v: string) => touched && !v.trim();

  const handleSubmit = async () => {
    setTouched(true);
    if (!form.employeeName.trim() || !form.email.trim() || !form.phone.trim() || !form.orgRole) {
      return;
    }
    const ok = await onSubmit(form);
    if (ok) onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-md rounded-xl bg-white dark:bg-gray-900 p-6 shadow-xl">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Invite Members</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">Invite your team by adding their details</p>
          </div>
          <button onClick={onClose} className="rounded p-1 text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-300">
            <X size={18} />
          </button>
        </div>

        <div className="mt-5 flex flex-col gap-4">
          <Input
            label="Employee Name"
            placeholder="Enter Employee Name"
            value={form.employeeName}
            onChange={(e) => setForm((prev) => ({ ...prev, employeeName: e.target.value }))}
            required={isMissing(form.employeeName)}
          />

          <Input
            label="Email Address"
            type="email"
            placeholder="Enter team member email"
            value={form.email}
            onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
            required={isMissing(form.email)}
          />

          <div className="flex flex-col gap-2 md:gap-1">
            <label className="text-[12px] font-medium text-[#434343] dark:text-gray-400">Phone Number</label>
            <div className="flex gap-2">
              <CountryCodeSelect
                value={form.countryCode}
                onChange={(value) => setForm((prev) => ({ ...prev, countryCode: value }))}
              />
              <div className="flex-1">
                <Input
                  placeholder="Type phone number"
                  type="tel"
                  value={form.phone}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, phone: e.target.value.replace(/\D/g, "") }))
                  }
                  required={isMissing(form.phone)}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-1" ref={roleRef}>
            <label className="text-[12px] font-medium text-[#434343] dark:text-gray-400">Role</label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setRoleOpen((s) => !s)}
                className={`flex h-[45px] w-full items-center justify-between rounded border-2 px-4 text-sm text-left ${
                  isMissing(form.orgRole) ? "border-[#FF0101]" : "border-[#E5E7EB]"
                }`}
              >
                <span className={form.orgRole ? "text-gray-700 dark:text-gray-300" : "text-[#7B7B7B] font-semibold dark:text-gray-500"}>
                  {form.orgRole ? ORG_ROLE_LABEL[form.orgRole] : "Select role"}
                </span>
                <ChevronDown size={16} className={`text-[#7B7B7B] font-semibold dark:text-gray-500 transition-transform ${roleOpen ? "rotate-180" : ""}`} />
              </button>
              {roleOpen && (
                <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg">
                  {ORG_ROLES.map((r) => (
                    <button
                      key={r.value}
                      type="button"
                      onClick={() => { setForm((prev) => ({ ...prev, orgRole: r.value })); setRoleOpen(false); }}
                      className={`block w-full px-4 py-2.5 text-left text-sm hover:bg-indigo-50 dark:hover:bg-indigo-500/15 ${
                        form.orgRole === r.value ? "bg-indigo-50 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-400" : "text-gray-700 dark:text-gray-300"
                      }`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {isMissing(form.orgRole) && (
              <span className="text-[#FF0101] dark:text-red-400 text-[12px] mt-1">This field is required</span>
            )}
          </div>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="mt-2 flex h-11 w-full items-center justify-center rounded bg-indigo-700 text-sm font-medium text-white transition hover:bg-indigo-800 disabled:opacity-60"
          >
            {loading ? "Inviting…" : "Invite"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex h-11 w-full items-center justify-center rounded border border-slate-300 dark:border-slate-700 text-sm font-medium text-[#332d27] dark:text-slate-300 transition hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Update Role modal ────────────────────────────────────────────────────────
const UpdateRoleModal: React.FC<{
  member: TeamDetails | null;
  onClose: () => void;
  onSubmit: (orgRole: string) => Promise<boolean>;
  loading: boolean;
}> = ({ member, onClose, onSubmit, loading }) => {
  const [orgRole, setOrgRole] = useState(member?.org_role || "");

  useEffect(() => {
    setOrgRole(member?.org_role || "");
  }, [member]);

  if (!member) return null;
  const displayName = `${member.first_name} ${member.last_name}`.trim() || member.email;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-sm rounded-xl bg-white dark:bg-gray-900 p-6 shadow-xl">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Update Role</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">Change {displayName}'s role</p>
          </div>
          <button onClick={onClose} className="rounded p-1 text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-300">
            <X size={18} />
          </button>
        </div>

        <div className="mt-5 flex flex-col gap-1.5">
          {ORG_ROLES.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => setOrgRole(r.value)}
              className={`w-full rounded-md border px-3 py-2.5 text-left text-sm transition ${
                orgRole === r.value
                  ? "border-indigo-400 bg-indigo-50 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-400"
                  : "border-slate-200 text-slate-700 hover:bg-slate-50"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        <div className="mt-5 flex flex-col gap-2">
          <button
            type="button"
            disabled={loading || !orgRole}
            onClick={async () => { const ok = await onSubmit(orgRole); if (ok) onClose(); }}
            className="flex h-11 w-full items-center justify-center rounded bg-indigo-700 text-sm font-medium text-white transition hover:bg-indigo-800 disabled:opacity-60"
          >
            {loading ? "Saving…" : "Save"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex h-11 w-full items-center justify-center rounded border border-slate-300 dark:border-slate-700 text-sm font-medium text-[#332d27] dark:text-slate-300 transition hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Delete Agent confirm modal ───────────────────────────────────────────────
const DeleteAgentModal: React.FC<{
  member: TeamDetails | null;
  onClose: () => void;
  onConfirm: () => Promise<boolean>;
  loading: boolean;
}> = ({ member, onClose, onConfirm, loading }) => {
  if (!member) return null;
  const displayName = `${member.first_name} ${member.last_name}`.trim() || member.email;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-sm rounded-xl bg-white dark:bg-gray-900 p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Delete agent?</h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {displayName} will lose access to this workspace. This can't be undone.
        </p>
        <div className="mt-5 flex flex-col gap-2">
          <button
            type="button"
            disabled={loading}
            onClick={async () => { const ok = await onConfirm(); if (ok) onClose(); }}
            className="flex h-11 w-full items-center justify-center rounded bg-rose-600 text-sm font-medium text-white transition hover:bg-rose-700 disabled:opacity-60"
          >
            {loading ? "Deleting…" : "Delete Agent"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex h-11 w-full items-center justify-center rounded border border-slate-300 dark:border-slate-700 text-sm font-medium text-[#332d27] dark:text-slate-300 transition hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Per-row actions dropdown ─────────────────────────────────────────────────
const RowActionsMenu: React.FC<{
  onUpdateRole: () => void;
  onDelete: () => void;
  // conversation_agent isn't allowed to change their own role (per direct
  // request) — rather than silently drop the menu item (which reads as a
  // bug), it stays visible but disabled with an explanation.
  disableUpdateRole?: boolean;
}> = ({ onUpdateRole, onDelete, disableUpdateRole }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        aria-label="Open actions"
        onClick={() => setOpen((s) => !s)}
        className="rounded p-1 transition hover:bg-slate-100 dark:hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      >
        <MoreIcon />
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-44 overflow-hidden rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg">
          <button
            type="button"
            disabled={disableUpdateRole}
            title={disableUpdateRole ? "You can't change your own role" : undefined}
            onClick={() => { if (disableUpdateRole) return; setOpen(false); onUpdateRole(); }}
            className={`block w-full px-3.5 py-2.5 text-left text-sm ${
              disableUpdateRole
                ? "text-slate-300 cursor-not-allowed"
                : "text-slate-700 hover:bg-slate-50"
            }`}
          >
            Update Role
          </button>
          <button
            type="button"
            onClick={() => { setOpen(false); onDelete(); }}
            className="block w-full px-3.5 py-2.5 text-left text-sm text-rose-600 hover:bg-rose-50"
          >
            Delete Agent
          </button>
        </div>
      )}
    </div>
  );
};

const Team = () => {
  const token = localStorage.getItem("authToken");
  const [team, setTeam] = useState<TeamDetails[]>([]);
  const [error] = useState<string | null>(null);
  const params = new URLSearchParams(new URL(window.location.href).search);
  const [currentPage, setCurrentPage] = useState(Number(params.get("page") || 1));
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(Number(params.get("per_page")) || 10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [defaultCountryCode, setDefaultCountryCode] = useState("+234");

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [roleModalMember, setRoleModalMember] = useState<TeamDetails | null>(null);
  const [deleteModalMember, setDeleteModalMember] = useState<TeamDetails | null>(null);
  // Used to block conversation_agent from updating their own role — see the
  // fetch below and the RowActionsMenu usage further down.
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  const { makeRequest: getTeamList, loading } = useRequest(`/profile/users`, "GET", {
    Authorization: `Bearer ${token}`,
  });

  const { makeRequest: getCurrentUser } = useRequest(`/auth/get_userdata`, "GET", {
    Authorization: `Bearer ${token}`,
  });

  const { makeRequest: teamInvite, loading: inviteLoading } = useRequest(`/profile/users`, "POST", {
    Authorization: `Bearer ${token}`,
  });

  const { makeRequest: updateRoleReq, loading: roleLoading } = useRequest(`/profile/users`, "PATCH", {
    Authorization: `Bearer ${token}`,
  });

  const { makeRequest: deleteAgentReq, loading: deleteLoading } = useRequest(`/profile/users`, "DELETE", {
    Authorization: `Bearer ${token}`,
  });

  function updateUrlParams(params: { [key: string]: string | number | null }) {
    const url = new URL(window.location.href);
    Object.keys(params).forEach((key) => {
      if (params[key]) url.searchParams.set(key, params[key]!.toString());
      else url.searchParams.delete(key);
    });
    window.history.pushState({}, "", url.toString());
  }

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    updateUrlParams({
      page: currentPage !== 1 ? currentPage : null,
      per_page: pageSize !== 10 ? pageSize : null,
      search: debouncedSearchTerm ? debouncedSearchTerm : null,
    });
    fetchTeam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, debouncedSearchTerm]);

  // Default the invite phone's country code to the logged-in admin's own
  // profile country (same source lead-qr-section.tsx uses) instead of
  // hardcoding Nigeria for everyone.
  useEffect(() => {
    (async () => {
      const [response] = await getCurrentUser();
      const code = response?.payload?.country_code;
      if (code) setDefaultCountryCode(normalizeDialCode(code));
      if (response?.payload?.user_id) setCurrentUserId(response.payload.user_id);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTeam = async () => {
    const queryParams: Record<string, any> = {
      page: currentPage,
      per_page: pageSize,
    };

    if (debouncedSearchTerm.trim()) {
      queryParams.search = debouncedSearchTerm.trim();
    }

    const [response] = await getTeamList(undefined, queryParams);
    let teamData: TeamDetails[] = [];

    if (Array.isArray(response)) {
      teamData = response;
    } else if (Array.isArray(response?.users)) {
      teamData = response.users;
    } else if (Array.isArray(response?.results)) {
      teamData = response.results;
    } else if (Array.isArray(response?.data)) {
      teamData = response.data;
    }
    setTeam(teamData);
    setTotalPages(Math.ceil(response?.total_pages));
  };

  function handlePageChange(page: number) {
    setCurrentPage(page);
  }

  const handleInvite = async (form: InviteFormState): Promise<boolean> => {
    const trimmedName = form.employeeName.trim();
    const spaceIdx = trimmedName.indexOf(" ");
    const first_name = spaceIdx === -1 ? trimmedName : trimmedName.slice(0, spaceIdx);
    const last_name = spaceIdx === -1 ? "" : trimmedName.slice(spaceIdx + 1).trim();

    const [response, status] = await teamInvite({
      email: form.email.trim(),
      first_name,
      last_name,
      role: "business_user",
      org_role: form.orgRole,
      phone_number: `${form.countryCode}${form.phone}`,
    });

    if (status && status >= 200 && status < 300) {
      toast.success("Invite sent");
      fetchTeam();
      return true;
    }
    toast.error(response?.message || "Could not send invite, please try again");
    return false;
  };

  const handleUpdateRole = async (orgRole: string): Promise<boolean> => {
    if (!roleModalMember) return false;
    const [response, status] = await updateRoleReq(
      { org_role: orgRole },
      undefined,
      `${roleModalMember.id}/role`
    );
    if (status && status >= 200 && status < 300) {
      toast.success("Role updated");
      fetchTeam();
      return true;
    }
    toast.error(response?.message || "Could not update role, please try again");
    return false;
  };

  const handleDeleteAgent = async (): Promise<boolean> => {
    if (!deleteModalMember) return false;
    const [response, status] = await deleteAgentReq(undefined, undefined, deleteModalMember.id);
    if (status && status >= 200 && status < 300) {
      toast.success("Agent removed");
      fetchTeam();
      return true;
    }
    toast.error(response?.message || "Could not delete agent, please try again");
    return false;
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-white dark:bg-gray-950 px-4 py-4 text-[#332d27] dark:text-gray-300 sm:px-6 lg:px-4">
        <h1 className="text-xl font-medium text-black dark:text-gray-100">Team</h1>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="min-h-screen bg-white dark:bg-gray-950 px-4 py-4 text-[#332d27] dark:text-gray-300 sm:px-6 lg:px-4">
        <h1 className="text-xl font-medium text-black dark:text-gray-100">Team</h1>
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <p className="text-red-500">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700"
          >
            Try Again
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-white dark:bg-gray-950 px-4 py-4 text-[#332d27] dark:text-gray-300 sm:px-6 lg:px-4">
      <h1 className="text-xl font-medium text-black dark:text-gray-100">Team</h1>

      <section className="mt-10 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <button
          onClick={() => setShowInviteModal(true)}
          className="inline-flex h-10 w-fit items-center gap-2 rounded bg-indigo-700 px-3 text-sm font-medium text-white shadow-sm transition hover:bg-indigo-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        >
          <span>Invite members</span>
          <UsersIcon />
        </button>

        <div className="grid gap-2 sm:grid-cols-[minmax(220px,1fr)_auto_auto] lg:w-[472px]">
          <label className="flex h-11 items-center gap-3 rounded-md border border-slate-400 dark:border-slate-600 px-3">
            <SearchIcon />
            <span className="sr-only">Search Team</span>
            <input
              className="w-full border-0 bg-transparent text-sm text-slate-900 dark:text-slate-100 outline-none placeholder:text-slate-500 dark:placeholder:text-slate-500"
              placeholder="Search Team"
              type="search"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </label>

          <button className="h-11 rounded-md border border-slate-400 dark:border-slate-600 px-3 text-sm font-medium text-[#332d27] dark:text-slate-300 transition hover:bg-slate-50 dark:hover:bg-slate-800">
            Select All
          </button>

          <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-slate-400 dark:border-slate-600 px-3 text-sm font-medium text-[#332d27] dark:text-slate-300 transition hover:bg-slate-50 dark:hover:bg-slate-800">
            <FilterIcon />
            <span>Filters</span>
          </button>
        </div>
      </section>

      <section className="mt-8 overflow-hidden">
        {team.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-500 dark:text-slate-400">No team members found</p>
          </div>
        ) : (
          <>
            <div className="hidden border-b border-slate-100 dark:border-slate-800 pb-5 text-sm font-medium md:grid md:grid-cols-[1.25fr_1.1fr_1.1fr_1.1fr_0.9fr_1.1fr_84px] md:items-center md:px-4">
              <div className="flex items-center gap-1">
                <span>Team Members</span>
                <ArrowUpIcon />
              </div>
              <span>Role</span>
              <span>Email Address</span>
              <span>Phone Number</span>
              <span>Status</span>
              <div className="flex items-center gap-1">
                <span>Tickets Assigned</span>
                <ArrowUpIcon />
              </div>
              <span>Actions</span>
            </div>

            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {team.map((member) => {
                const fullName = `${member.first_name} ${member.last_name}`.trim();
                const displayName = fullName || member.email;
                const roleLabel = (member.org_role && ORG_ROLE_LABEL[member.org_role])
                  || member.role?.replace(/_/g, ' ')
                  || 'N/A';

                return (
                  <article
                    key={member.id}
                    className="grid gap-4 py-5 md:grid-cols-[1.25fr_1.1fr_1.1fr_1.1fr_0.9fr_1.1fr_84px] md:items-center md:px-4"
                  >
                    <div className="flex items-center gap-3">
                      <Avatar name={displayName} />
                      <span className="text-sm font-medium">{displayName}</span>
                    </div>

                    <div className="grid grid-cols-2 gap-1 text-sm md:block">
                      <span className="font-medium text-slate-500 dark:text-slate-400 md:hidden">Role</span>
                      <span className="font-semibold md:font-medium capitalize">
                        {roleLabel}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-1 text-sm md:block">
                      <span className="font-medium text-slate-500 dark:text-slate-400 md:hidden">Email</span>
                      <span className="font-semibold md:font-medium break-all">
                        {member.email}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-1 text-sm md:block">
                      <span className="font-medium text-slate-500 dark:text-slate-400 md:hidden">Phone</span>
                      <span className="font-semibold md:font-medium">
                        {member.phone_number || 'N/A'}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-1 text-sm md:block">
                      <span className="font-medium text-slate-500 dark:text-slate-400 md:hidden">Status</span>
                      <StatusBadge status={member.status} />
                    </div>

                    <div className="grid grid-cols-2 gap-1 text-sm md:block">
                      <span className="font-medium text-slate-500 dark:text-slate-400 md:hidden">Tickets</span>
                      <a
                        className="font-medium text-indigo-700 dark:text-indigo-400 underline"
                        href={`/tickets?assignee=${member.id}`}
                      >
                        0
                      </a>
                    </div>

                    <div className="flex items-center justify-between md:justify-start">
                      <span className="text-sm font-medium text-slate-500 dark:text-slate-400 md:hidden">Actions</span>
                      <RowActionsMenu
                        onUpdateRole={() => setRoleModalMember(member)}
                        onDelete={() => setDeleteModalMember(member)}
                        disableUpdateRole={isConversationAgent() && member.id === currentUserId}
                      />
                    </div>
                  </article>
                );
              })}
            </div>
          </>
        )}
      </section>
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
        />
      )}

      <InviteModal
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSubmit={handleInvite}
        defaultCountryCode={defaultCountryCode}
        loading={inviteLoading}
      />

      <UpdateRoleModal
        member={roleModalMember}
        onClose={() => setRoleModalMember(null)}
        onSubmit={handleUpdateRole}
        loading={roleLoading}
      />

      <DeleteAgentModal
        member={deleteModalMember}
        onClose={() => setDeleteModalMember(null)}
        onConfirm={handleDeleteAgent}
        loading={deleteLoading}
      />
    </main>
  );
};

export default Team;
